import apiClient from "@/api-client";
import { useQuery } from "@tanstack/react-query";

/**
 * A programme is the heading a client calls "the campaign" — Poly Video · APAC — with one
 * ordinary campaign per market underneath it. It groups and reports; it never dials anything.
 *
 * Everything here is read-only. The rows are produced by provisioning a confirmed campaign
 * request, so there is nothing on this surface to create or edit — the live screen only reads.
 *
 * Paths never start with `/api`: the axios baseURL already ends in it, so `/api/campaign-programmes`
 * resolves to `/api/api/campaign-programmes` and 404s.
 *
 * Unlike the campaign-request money columns, every figure below is a real `number`. The API
 * parses the aggregate's raw counts once, on the server, and `reachRate` is recomputed from the
 * totals rather than averaged across markets — a 20-lead market must not weigh the same as a
 * 2,000-lead one.
 */

/** The five figures every slice of a programme is measured by. */
export interface ProgrammeStats {
	/** Outbound calls placed. */
	dialed: number;
	/** Calls that connected to a person. */
	reached: number;
	/** `reached / dialed` as a percentage, already rounded to two places by the API. */
	reachRate: number;
	/** Connected seconds. */
	talkSeconds: number;
	/**
	 * Calls whose recorded outcome is flagged as a conversion. Null on calls placed before
	 * per-call dispositions were stored, so historic campaigns under-report rather than guess.
	 */
	booked: number;
}

/** One market's slice of a programme. */
export interface ProgrammeMemberStats extends ProgrammeStats {
	campaignId: string;
	title: string;
	/** The market campaign's own zone. Its day boundaries are cut in this, not in the programme's. */
	timezone: string;
	agentId: string | null;
}

/** One day of dialing inside one market, bucketed in that market's own timezone. */
export interface ProgrammeDayStats extends ProgrammeStats {
	/** `YYYY-MM-DD`, already local to the campaign. Never re-parse it into a UTC instant. */
	day: string;
}

export interface CampaignProgrammeRollup {
	members: ProgrammeMemberStats[];
	total: ProgrammeStats;
}

export interface CampaignProgramme {
	id: string;
	title: string;
	/** The one zone the rollup is stated in. Four markets have four day boundaries; this picks one. */
	reportingTimezone: string;
	maxConcurrentCalls: number | null;
	createdAt: string;
}

/** The member campaign itself, as the programme's own campaigns endpoint returns it. */
export interface CampaignProgrammeMember {
	id: string;
	title: string;
	enabled: boolean;
	status: string;
	timezone: string;
	agent: { id: string; name: string } | null;
	campaignProgrammeId: string | null;
	startAt: string | null;
	endAt: string | null;
	createdAt: string;
	deleted: boolean;
}

export const campaignProgrammesKeys = {
	all: ["campaignProgrammes"] as const,
	details: () => [...campaignProgrammesKeys.all, "detail"] as const,
	detail: (id: string) => [...campaignProgrammesKeys.details(), id] as const,
	campaigns: (id: string) => [...campaignProgrammesKeys.detail(id), "campaigns"] as const,
	rollup: (id: string) => [...campaignProgrammesKeys.detail(id), "rollup"] as const,
	days: (id: string, campaignId: string) => [...campaignProgrammesKeys.detail(id), "days", campaignId] as const,
};

/** The heading itself — its title, and the zone its rollup is stated in. */
export function useCampaignProgramme(id: string | null | undefined) {
	return useQuery({
		queryKey: campaignProgrammesKeys.detail(id ?? "none"),
		queryFn: async (): Promise<CampaignProgramme> => {
			const response = await apiClient.get(`/campaign-programmes/${id}`);
			return response.data;
		},
		enabled: Boolean(id),
	});
}

/**
 * The member campaigns — one per market. The rollup names each market by campaign id only, so
 * this is what turns an id into an agent name and a schedule.
 */
export function useCampaignProgrammeCampaigns(id: string | null | undefined) {
	return useQuery({
		queryKey: campaignProgrammesKeys.campaigns(id ?? "none"),
		queryFn: async (): Promise<CampaignProgrammeMember[]> => {
			const response = await apiClient.get(`/campaign-programmes/${id}/campaigns`);
			return response.data;
		},
		enabled: Boolean(id),
	});
}

/**
 * Per-market figures plus the programme total, in one query on the API side.
 *
 * Polled while a programme is dialing: the whole point of the screen is that a client never has
 * to ask how it is going, and a figure that only moves on reload is a figure they will ask about.
 */
export function useCampaignProgrammeRollup(id: string | null | undefined, options?: { refetchMs?: number }) {
	return useQuery({
		queryKey: campaignProgrammesKeys.rollup(id ?? "none"),
		queryFn: async (): Promise<CampaignProgrammeRollup> => {
			const response = await apiClient.get(`/campaign-programmes/${id}/rollup`);
			return response.data;
		},
		enabled: Boolean(id),
		refetchInterval: options?.refetchMs ?? false,
	});
}

/**
 * The day drill-down for one market — "click yesterday, see what happened".
 *
 * Derived entirely from call timestamps, so it answers for campaigns that ran long before
 * programmes existed. Only `booked` is thin on those: it reads a per-call disposition that
 * historic rows do not carry.
 */
export function useCampaignProgrammeDays(
	programmeId: string | null | undefined,
	campaignId: string | null | undefined
) {
	return useQuery({
		queryKey: campaignProgrammesKeys.days(programmeId ?? "none", campaignId ?? "none"),
		queryFn: async (): Promise<ProgrammeDayStats[]> => {
			const response = await apiClient.get(`/campaign-programmes/${programmeId}/campaigns/${campaignId}/days`);
			return response.data;
		},
		enabled: Boolean(programmeId) && Boolean(campaignId),
	});
}
